// Shipments dashboard section: KPI row + dual-axis cost/volume chart + sortable top-partners
// table. Role (consignee/carrier) switch is shown only when both roles are available.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import { ShipmentRolesAvailabilityDTO, ShipmentStatisticsDTO, ShipmentTopRowDTO } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { formatNumber } from '@utils/utils.tsx';
import { formatPrice } from '@utils/priceUtils';
import { useDashboardPref } from '../dashboardPrefs.tsx';
import SvgTruck from '@icons/truck.svg?react';
import DashboardSelect from '../components/DashboardSelect.tsx';
import DashboardSection from '../components/DashboardSection.tsx';
import DashboardKpiCard from '../components/DashboardKpiCard.tsx';
import DashboardTable, { DashboardColumn } from '../components/DashboardTable.tsx';
import DashboardCard from '../components/DashboardCard.tsx';
import DashboardChartTitle from '../components/DashboardChartTitle.tsx';
import RingilChart from '../charts/RingilChart.tsx';
import { axisTooltipFormatter, chartColors } from '../charts/chartTheme.ts';
import { alignZeroAxes, isChartDataEmpty, timeSeriesZoom } from '../charts/chartOptions.ts';
import { ShipmentRole, ShipmentTopSortBy, SortDir, StatsRange } from '../dashboardTypes.ts';

interface ShipmentsSectionProps {
  availability: ShipmentRolesAvailabilityDTO;
}

const ShipmentsSection: React.FC<ShipmentsSectionProps> = ({ availability }) => {
  const { tr } = useTranslationLgs();
  const { getDashboardSectionShipments, getDashboardSectionShipmentsTopPartners } = useContext(ApiContext);

  const bothRoles = availability.asConsignee && availability.asCarrier;
  const [storedRole, setRole] = useDashboardPref<ShipmentRole>('shipments.role', availability.asConsignee ? ShipmentRole.CONSIGNEE : ShipmentRole.CARRIER);
  // The stored role may no longer be available (permissions changed, different account on the same
  // browser) — clamp it to an available one, otherwise the section queries an empty variant.
  const role = bothRoles ? storedRole : availability.asConsignee ? ShipmentRole.CONSIGNEE : ShipmentRole.CARRIER;
  // Shared across all sections so the 30 days / 6 months switch ties them together.
  const [range, setRange] = useDashboardPref<StatsRange>('dashboard.range', StatsRange.D30);
  const [sortBy, setSortBy] = useDashboardPref<ShipmentTopSortBy>('shipments.sortBy', ShipmentTopSortBy.SHIPMENTS);
  const [sortDir, setSortDir] = useDashboardPref<SortDir>('shipments.sortDir', SortDir.DESC);

  const [stats, setStats] = useState<ShipmentStatisticsDTO>();
  const [partners, setPartners] = useState<ShipmentTopRowDTO[]>([]);

  useEffect(() => {
    getDashboardSectionShipments(role, range, data => setStats(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range]);

  useEffect(() => {
    getDashboardSectionShipmentsTopPartners(role, range, sortBy, sortDir, data => setPartners(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range, sortBy, sortDir]);

  const onSort = (key: ShipmentTopSortBy) => {
    if (key === sortBy) {
      setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
    } else {
      setSortBy(key);
      setSortDir(SortDir.DESC);
    }
  };

  const currency = stats?.currency ?? '';
  const comparison = range === StatsRange.D30 ? tr('dashboard.vsPrior30Days', 'vs. prior 30 days') : tr('dashboard.vsPrior6Months', 'vs. prior 6 months');

  const origLabel = tr('dashboard.shipments.originalCost', 'Transport cost');
  const extraLabel = tr('dashboard.shipments.extraCost', 'Extra cost');
  const countLabel = tr('dashboard.shipments.count', 'Shipments');

  // Compact currency axis labels with translatable magnitude abbreviations (en: M / k; cs: mil / tis).
  const unitMillion = tr('dashboard.chart.unitMillion', 'M');
  const unitThousand = tr('dashboard.chart.unitThousand', 'k');
  const formatAxisCurrency = (v: number) => {
    if (Math.abs(v) >= 1_000_000) return `${formatNumber(v / 1_000_000, 1)} ${unitMillion}`;
    if (Math.abs(v) >= 1000) return `${formatNumber(v / 1000, 0)} ${unitThousand}`;
    return formatNumber(v, 0);
  };

  const chartOption = useMemo(() => {
    const ot = stats?.overTime ?? [];
    // The bars are stacked, so the cost axis has to be sized against the stack totals — and both
    // axes get a shared zero level so the line and the bars are read against the same baseline.
    const [costAxis, countAxis] = alignZeroAxes(
      ot.map(b => (b.originalCost ?? 0) + (b.extraCost ?? 0)),
      ot.map(b => b.count),
    );
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter((v, name) => (name === countLabel ? formatNumber(v, 0) : formatPrice(v, currency, 0))),
      },
      legend: { top: 0, right: 0, data: [origLabel, extraLabel, countLabel] },
      // Top room for the axis name + legend; bottom room for the zoom slider.
      grid: { left: 8, right: 8, top: 40, bottom: 56, containLabel: true },
      ...timeSeriesZoom,
      xAxis: { type: 'category', data: ot.map(b => b.bucket) },
      yAxis: [
        // Currency unit as the axis name (kept connected to the axis), right-aligned so it sits
        // above the value-label column rather than out in the plot.
        { type: 'value', ...costAxis, name: currency + '  ' || 'EUR  ', nameLocation: 'end', nameGap: 14, nameTextStyle: { align: 'right', color: chartColors.tertiary }, axisLabel: { formatter: formatAxisCurrency } },
        // Count axis: numbers only (the line is already labelled in the legend).
        { type: 'value', ...countAxis, splitLine: { show: false } },
      ],
      series: [
        // Stacked bars for prices...
        { name: origLabel, type: 'bar', stack: 'cost', yAxisIndex: 0, color: chartColors.processing, data: ot.map(b => b.originalCost), barMaxWidth: 40 },
        { name: extraLabel, type: 'bar', stack: 'cost', yAxisIndex: 0, color: chartColors.processingMedium, data: ot.map(b => b.extraCost), barMaxWidth: 40 },
        // ...and a line for the count on the secondary axis.
        { name: countLabel, type: 'line', yAxisIndex: 1, smooth: true, symbolSize: 6, color: chartColors.brand, data: ot.map(b => b.count) },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, currency, origLabel, extraLabel, countLabel]);

  const chartEmpty = isChartDataEmpty((stats?.overTime ?? []).flatMap(b => [b.originalCost, b.extraCost, b.count]));

  const columns: DashboardColumn<ShipmentTopRowDTO, ShipmentTopSortBy>[] = [
    { key: 'name', label: tr('dashboard.shipments.partner', 'Carrier'), sortKey: ShipmentTopSortBy.NAME, render: r => r.companyName ?? '—' },
    { key: 'shipments', label: countLabel, sortKey: ShipmentTopSortBy.SHIPMENTS, align: 'right', render: r => formatNumber(r.shipments, 0) },
    { key: 'totalCosts', label: tr('dashboard.shipments.totalCosts', 'Total costs'), sortKey: ShipmentTopSortBy.TOTAL_COSTS, align: 'right', render: r => formatPrice(r.totalCosts, currency, 0) },
    { key: 'extraCosts', label: tr('dashboard.shipments.extraCosts', 'Extra costs'), sortKey: ShipmentTopSortBy.EXTRA_COSTS, align: 'right', render: r => formatPrice(r.extraCosts, currency, 0) },
    { key: 'pricePerKm', label: tr('dashboard.shipments.pricePerKm', 'Price / km'), sortKey: ShipmentTopSortBy.PRICE_PER_KM, align: 'right', render: r => (r.pricePerKm != null ? formatPrice(r.pricePerKm, currency, 1) : '—') },
    { key: 'pricePerUnit', label: tr('dashboard.shipments.pricePerUnit', 'Price / unit'), sortKey: ShipmentTopSortBy.PRICE_PER_UNIT, align: 'right', render: r => (r.pricePerUnit != null ? formatPrice(r.pricePerUnit, currency, 1) : '—') },
  ];

  const controls = (
    <>
      {bothRoles && (
        <DashboardSelect
          value={role}
          onChange={setRole}
          options={[
            { code: ShipmentRole.CONSIGNEE, label: tr('dashboard.role.consignee', 'As consignee') },
            { code: ShipmentRole.CARRIER, label: tr('dashboard.role.carrier', 'As carrier') },
          ]}
        />
      )}
      <DashboardSelect
        value={range}
        onChange={setRange}
        options={[
          { code: StatsRange.D30, label: tr('dashboard.range.d30', '30 days') },
          { code: StatsRange.M6, label: tr('dashboard.range.m6', '6 months') },
        ]}
      />
    </>
  );

  return (
    <DashboardSection title={tr('dashboard.shipments.title', 'Shipments')} icon={<SvgTruck />} controls={controls}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <DashboardKpiCard
          label={tr('dashboard.shipments.totalShipments', 'Total shipments')}
          value={formatNumber(stats?.totalShipments?.current ?? 0, 0)}
          changePct={stats?.totalShipments?.changePct}
          comparisonLabel={comparison}
          tooltip={tr('dashboard.shipments.totalShipmentsTooltip', 'Number of shipments in the selected period.')}
        />
        <DashboardKpiCard
          label={tr('dashboard.shipments.totalCosts', 'Total costs')}
          value={formatPrice(stats?.totalCosts?.current ?? 0, currency, 0)}
          changePct={stats?.totalCosts?.changePct}
          comparisonLabel={comparison}
          tooltip={tr('dashboard.shipments.totalCostsTooltip', 'Total transport cost in the selected period.')}
        />
        <DashboardKpiCard
          label={tr('dashboard.shipments.extraCosts', 'Extra costs')}
          value={formatPrice(stats?.extraCosts?.current ?? 0, currency, 0)}
          changePct={stats?.extraCosts?.changePct}
          positiveIsGood={false}
          comparisonLabel={comparison}
          tooltip={tr('dashboard.shipments.extraCostsTooltip', 'Additional costs on top of the agreed transport price.')}
        />
        {stats?.savings && (
          <DashboardKpiCard
            aiStyled
            label={tr('dashboard.shipments.savings', 'Estimated savings')}
            value={formatPrice(stats.savings.current ?? 0, currency, 0)}
            changePct={stats.savings.changePct}
            comparisonLabel={comparison}
            tooltip={tr('dashboard.shipments.savingsTooltip', 'AI-estimated savings achieved in the selected period.')}
          />
        )}
      </div>

      <DashboardCard>
        <DashboardChartTitle title={tr('dashboard.shipments.chartTitle', 'Shipments')} />
        <RingilChart option={chartOption} height={320} notMerge isEmpty={chartEmpty} />
      </DashboardCard>

      <DashboardCard>
        <DashboardChartTitle title={tr('dashboard.shipments.topPartners', 'Top carriers')} />
        <DashboardTable columns={columns} rows={partners} rowKey={(r, i) => r.companyId ?? `${i}`} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      </DashboardCard>
    </DashboardSection>
  );
};

export default ShipmentsSection;